import type { ComponentNode } from "../layout/types.js";
import { applyPatchToBarLoaderNode } from "./applyRuntimePatches.js";
import {
  applyCompiledBarLoaderVariableFootprint,
  resolveCompiledBarLoaderTotalWeight,
} from "./barLoaderHelpers.js";
import { collectBehaviorDependencies } from "./compileEvents.js";
import { compileBehavior } from "./compileBehavior.js";
import {
  dependencyFromDataBindingPath,
  evaluateWithRenderCache,
} from "./renderCache.js";
import {
  applyRuntimeDomTargetFlags,
  runCompiledNodeBehavior,
  withCurrentNodeGeometry,
} from "./applyNodeBehavior.js";
import type {
  CompiledBarLoaderNode,
  CompileNodeContext,
  RenderBarLoaderNode,
  RenderBarLoaderProps,
  RuntimeContext,
} from "./types.js";
import { compileNodeStyle, transitionsTouchTopLeft } from "./helpers.js";
import { uniqueDependencies } from "./method.js";

export function compileBarLoaderNode(
  node: ComponentNode,
  compileContext: CompileNodeContext,
): CompiledBarLoaderNode {
  if (node.kind !== "component" || node.component !== "barLoader") {
    throw new Error("Node is not a barLoader component");
  }

  const baseStyle = compileNodeStyle(node.style, node.transitions);
  const baseProps = (node.props ?? {}) as RenderBarLoaderProps;
  const behavior = node.behavior
    ? compileBehavior(
        node.behavior,
        node.id,
        compileContext.catalogDeps,
        {
          ownerIsBarLoader: true,
        },
        compileContext.layoutNodeById,
      )
    : undefined;

  const dependencies = uniqueDependencies([
    ...(behavior ? collectBehaviorDependencies(behavior, compileContext) : []),
    ...(node.dataBinding?.path
      ? [dependencyFromDataBindingPath(node.dataBinding.path)]
      : []),
  ]);

  return {
    id: node.id,
    dependencies,

    evaluate(ctx: RuntimeContext): RenderBarLoaderNode | null {
      return evaluateWithRenderCache(ctx, node.id, dependencies, () => {
        let renderNode: RenderBarLoaderNode = {
          id: node.id,
          key: ctx.__nodeInstanceKeyPrefix
            ? `${ctx.__nodeInstanceKeyPrefix}${node.id}`
            : node.id,
          x: node.x,
          y: node.y,
          width: node.width,
          height: node.height,
          zIndex: node.zIndex,
          rotation: node.rotation ?? 0,
          kind: "barLoader",
          props: { ...baseProps },
          style: { ...baseStyle },
          hasTopLeftTransition: transitionsTouchTopLeft(node.transitions),
          barLoaderTargetPatches: [],
        };

        if (behavior) {
          const behaviorCtx = withCurrentNodeGeometry(ctx, {
            x: node.x,
            y: node.y,
            width: node.width,
            height: node.height,
            rotation: node.rotation ?? 0,
          });
          const { patches, effects, continuations, runtimeDomTarget } =
            runCompiledNodeBehavior(behavior, behaviorCtx, node.id);

          for (const patch of patches) {
            renderNode = applyPatchToBarLoaderNode(renderNode, patch);
          }

          ctx.__runtimeEffects?.push(...effects);
          ctx.__runtimeContinuations?.push(...continuations);
          renderNode = applyRuntimeDomTargetFlags(
            renderNode,
            runtimeDomTarget,
            behavior,
          );
        }

        const totalWeight = resolveCompiledBarLoaderTotalWeight(
          renderNode.props,
          node.dataBinding,
          ctx,
        );

        renderNode = {
          ...renderNode,
          props: {
            ...renderNode.props,
            totalWeight,
          },
        };

        renderNode = applyCompiledBarLoaderVariableFootprint(
          renderNode,
          totalWeight,
        );

        if (renderNode.style.visibility === false) {
          return null;
        }

        return renderNode;
      });
    },
  };
}
